import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Target, Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function VisionBoard({ eventId, isOwner }) {
  const [visions, setVisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    image_url: "",
    category: "personal",
    added_by: ""
  });

  useEffect(() => {
    fetchVisions();
  }, [eventId]);

  const fetchVisions = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/vision-board`);
      setVisions(response.data);
    } catch (error) {
      console.error("Failed to fetch visions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.added_by) {
      toast.error("Titel und Name sind erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/vision-board`,
        formData
      );
      
      toast.success("Vision hinzugefügt!");
      setFormData({ title: "", description: "", image_url: "", category: "personal", added_by: "" });
      fetchVisions();
    } catch (error) {
      console.error("Failed to add vision:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleDelete = async (visionId) => {
    if (!confirm("Vision wirklich löschen?")) return;
    
    try {
      await axios.delete(
        `${API}/events/${eventId}/vision-board/${visionId}`,
        { withCredentials: true }
      );
      toast.success("Vision gelöscht");
      fetchVisions();
    } catch (error) {
      console.error("Failed to delete vision:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const categories = [
    { value: "personal", label: "🧘 Persönlich" },
    { value: "relationship", label: "❤️ Beziehung" },
    { value: "career", label: "💼 Karriere" },
    { value: "health", label: "💪 Gesundheit" },
    { value: "financial", label: "💰 Finanzen" },
    { value: "adventure", label: "🌟 Abenteuer" }
  ];

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-primary" />
          Vision Board
        </h3>
        <p className="text-sm text-muted-foreground">
          Visualisiert eure gemeinsamen Träume und Ziele
        </p>
      </div>

      {/* Add Vision Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
        <div>
          <Label>Vision/Ziel *</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="z.B. Haus kaufen, Weltreise machen"
            required
          />
        </div>

        <div>
          <Label>Beschreibung</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Details zu dieser Vision..."
            rows={2}
          />
        </div>

        <div>
          <Label className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Bild-URL (optional)
          </Label>
          <Input
            type="url"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            placeholder="https://..."
          />
          <p className="text-xs text-muted-foreground mt-1">
            Link zu einem inspirierenden Bild
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Kategorie</Label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-background"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Dein Name *</Label>
            <Input
              value={formData.added_by}
              onChange={(e) => setFormData({ ...formData, added_by: e.target.value })}
              placeholder="Name"
              required
            />
          </div>
        </div>

        <Button type="submit" className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Vision hinzufügen
        </Button>
      </form>

      {/* Vision Board Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visions.length === 0 ? (
          <div className="col-span-2 text-center py-8 text-muted-foreground">
            <Target className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Euer Vision Board ist noch leer</p>
            <p className="text-xs mt-1">Fügt eure ersten Visionen hinzu!</p>
          </div>
        ) : (
          visions.map((vision) => {
            const category = categories.find(c => c.value === vision.category);
            
            return (
              <div 
                key={vision.id} 
                className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg border overflow-hidden"
              >
                {vision.image_url && (
                  <div className="h-40 bg-secondary relative overflow-hidden">
                    <img 
                      src={vision.image_url} 
                      alt={vision.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full text-muted-foreground"><svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                      }}
                    />
                  </div>
                )}
                
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span>{category?.label?.split(' ')[0]}</span>
                        <h4 className="font-medium">{vision.title}</h4>
                      </div>
                      {vision.description && (
                        <p className="text-sm text-muted-foreground">{vision.description}</p>
                      )}
                    </div>
                    {isOwner && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDelete(vision.id)}
                        className="h-8 w-8 text-destructive flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">von {vision.added_by}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}